package com.flowyride.data.account

import com.flowyride.core.api.AccountApi
import com.flowyride.core.domain.account.SessionState
import com.flowyride.core.domain.account.User
import io.ktor.client.HttpClient
import io.ktor.client.plugins.api.createClientPlugin
import io.ktor.client.request.bearerAuth
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * The signed-in state shared by every call to the FlowyRide server.
 * The token (native apps) is only ever attached by [withServerSession], i.e. to the server's own client.
 */
class ServerSession(private val tokenStore: TokenStore) {
    private val _state = MutableStateFlow<SessionState>(SessionState.Unknown)
    val state: StateFlow<SessionState> = _state.asStateFlow()

    val token: String? get() = tokenStore.read()

    fun signedIn(user: User, token: String?) {
        token?.let(tokenStore::write)
        _state.value = SessionState.SignedIn(user)
    }

    fun signedOut() {
        tokenStore.write(null)
        _state.value = SessionState.SignedOut
    }

    /** The server refused the session (expired, revoked): back to the sign-in screen. */
    internal fun rejected() {
        if (_state.value is SessionState.SignedIn) signedOut() else tokenStore.write(null)
    }
}

/**
 * Client for the FlowyRide server only: adds the session token and signs out when the server refuses it.
 * Never use it for third-party APIs (Photon, data.gouv): they must not receive the token.
 */
fun HttpClient.withServerSession(session: ServerSession): HttpClient = config {
    install(
        createClientPlugin("FlowyRideSession") {
            onRequest { request, _ -> session.token?.let { request.bearerAuth(it) } }
            onResponse { response ->
                val path = response.call.request.url.encodedPath
                // A wrong password also answers 401: that is not an expired session.
                val checksPassword = path.endsWith(AccountApi.LOGIN) || path.endsWith(AccountApi.PASSWORD)
                if (response.status == HttpStatusCode.Unauthorized && !checksPassword) session.rejected()
            }
        },
    )
}
